import { useState, useCallback, useEffect, useRef } from 'react'
import {
  Box, Typography, IconButton, Tooltip, CircularProgress, Chip, Checkbox,
} from '@mui/material'
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import StatePicker from '../../comps/StatePicker'
import TaWordLimit from '../../comps/TaWordLimit'
import ConfirmModal from '../modals/ConfirmModal'
import SectionedQuestionList, {
  type FlatItem, type SectionMeta,
} from '../../comps/SectionedQuestionList'
import { pmDelQuestion } from '../../services/ProjectManagerService'
import { addToast } from '../../services/Toasts'
import { FS_SM, FS_XS, ICON_MD } from '../../common/theme'
import type { Project, Question } from '../../types'

let _sectionCounter = 0

function buildFlatState(questions: Question[]): { items: FlatItem[]; sections: SectionMeta[] } {
  const sections: SectionMeta[] = []
  const nameToId = new Map<string, string>()
  const items: FlatItem[] = []
  let currentSection: string | null = null

  for (const q of questions) {
    const secName: string | null = (q as any).section ?? null
    if (secName !== currentSection) {
      currentSection = secName
      if (secName) {
        if (!nameToId.has(secName)) {
          const id = `sec${++_sectionCounter}`
          nameToId.set(secName, id)
          sections.push({ id, name: secName })
        }
        items.push({ type: 'section', sectionId: nameToId.get(secName)! })
      }
    }
    items.push({ type: 'question', sha: q.questionsha })
  }

  return { items, sections }
}

interface BidManageQuestionsTabProps {
  project: Project
  onQuestionChange?: (q: Question) => void
  onProjectSave?: (p: Project) => void
}

export default function BidManageQuestionsTab({
  project, onQuestionChange, onProjectSave,
}: BidManageQuestionsTabProps) {

  const qMap = Object.fromEntries((project?.questions ?? []).map(q => [q.questionsha, q]))

  const [items, setItems] = useState<FlatItem[]>(() => buildFlatState(project?.questions ?? []).items)
  const [sections, setSections] = useState<SectionMeta[]>(() => buildFlatState(project?.questions ?? []).sections)
  const [selectedShas, setSelectedShas] = useState<Set<string>>(new Set())
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [bulkSaving, setBulkSaving] = useState(false)

  // Refs mirror the latest items / sections so callbacks fired after
  // setItems / setSections (notably SectionedQuestionList's onChange,
  // which runs in the same tick as the child's state update) can read
  // the fresh values without a stale closure.
  const itemsRef = useRef(items)
  const sectionsRef = useRef(sections)
  useEffect(() => { itemsRef.current = items }, [items])
  useEffect(() => { sectionsRef.current = sections }, [sections])

  // Sync from project on external changes (refresh, undo, bulk delete,
  // taskstream patches). Local state is now a pure derivation of
  // project.questions - every user action commits immediately - so
  // there's no "unsaved order" gate to worry about.
  useEffect(() => {
    const { items: newItems, sections: newSections } = buildFlatState(project?.questions ?? [])
    setItems(newItems)
    setSections(newSections)
  }, [project?.questions]) // eslint-disable-line react-hooks/exhaustive-deps

  // Rebuild the project's questions array from the current flat
  // item-and-section ordering + section name lookup, then push it up
  // through onProjectSave with `modified: true` so the Save Bid
  // affordance picks the change up. The actual persistence happens
  // when the user clicks Save Bid (or autosave fires) - there's no
  // longer a per-action "Save Order" button.
  const commitOrder = (nextItems: FlatItem[], nextSections: SectionMeta[]) => {
    let currentSectionName: string | undefined
    const sectionOf: Record<string, string | undefined> = {}
    for (const item of nextItems) {
      if (item.type === 'section') {
        currentSectionName = nextSections.find(s => s.id === item.sectionId)?.name
      } else {
        sectionOf[item.sha] = currentSectionName
      }
    }
    const orderedShas = nextItems
      .filter(i => i.type === 'question')
      .map(i => (i as Extract<FlatItem, { type: 'question' }>).sha)
    const reordered = orderedShas
      .map(sha => { const q = qMap[sha]; return q ? { ...q, section: sectionOf[sha] ?? null } : null })
      .filter(Boolean) as Question[]
    const inItems = new Set(orderedShas)
    const remainder = (project?.questions ?? []).filter(q => !inItems.has(q.questionsha))
    onProjectSave?.({ ...project, questions: [...reordered, ...remainder], modified: true })
  }

  // ── Selection helpers ─────────────────────────────────────────────────────
  const allQuestionShas = items
    .filter(i => i.type === 'question')
    .map(i => (i as Extract<FlatItem, { type: 'question' }>).sha)
  const allSelected = allQuestionShas.length > 0 && allQuestionShas.every(s => selectedShas.has(s))
  const selectedCount = selectedShas.size

  const toggleSelect = (sha: string) => {
    setSelectedShas(prev => {
      const next = new Set(prev)
      if (next.has(sha)) next.delete(sha); else next.add(sha)
      return next
    })
  }

  const toggleSelectAll = () => {
    setSelectedShas(allSelected ? new Set() : new Set(allQuestionShas))
  }

  // ── Bulk actions ──────────────────────────────────────────────────────────
  const bulkApply = (patch: (q: Question) => Partial<Question>) => {
    selectedShas.forEach(sha => {
      const q = qMap[sha]
      if (q) onQuestionChange?.({ ...q, ...patch(q) })
    })
  }

  const handleBulkState = (state: string) => bulkApply(() => ({ state }))
  const handleBulkWordLimit = (rl: any) => bulkApply(() => ({ replylimit: rl }))

  const handleBulkDelete = useCallback(async () => {
    const shas = Array.from(selectedShas)
    if (shas.length === 0) return
    setBulkSaving(true)
    try {
      if (!project) return;
      await Promise.all(shas.map(sha => pmDelQuestion(sha, project?.projectsha)))
      // Strip from local item ordering and from the parent's project.
      setItems(prev => prev.filter(it => !(it.type === 'question' && shas.includes(it.sha))))
      const filtered = (project.questions ?? []).filter(q => !shas.includes(q.questionsha))
      onProjectSave?.({ ...project, questions: filtered })
      setSelectedShas(new Set())
      addToast('success', `Deleted ${shas.length} question${shas.length !== 1 ? 's' : ''}`)
    } catch {
      addToast('error', 'Failed to delete selected questions')
    } finally {
      setBulkSaving(false)
      setDeleteOpen(false)
    }
  }, [selectedShas, project, onProjectSave, addToast])

  // Sections - Add is local-only (an empty section has no project-level
  // effect until a question is dragged into it; the drag will commit
  // the section assignment then). Delete + reorder commit straight away.
  const addSection = () => {
    const id = `sec${++_sectionCounter}`
    const name = `Section ${sections.length + 1}`
    setSections(prev => [...prev, { id, name }])
    setItems(prev => [{ type: 'section', sectionId: id }, ...prev])
  }

  const deleteSection = (id: string) => {
    const newSections = sections.filter(s => s.id !== id)
    const newItems = items.filter(item => !(item.type === 'section' && item.sectionId === id))
    setSections(newSections)
    setItems(newItems)
    commitOrder(newItems, newSections)
  }

  const handleNumberingCommit = (sha: string, value: string) => {
    const q = qMap[sha]
    if (!q) return
    if ((q.numbering ?? '') === value) return
    onQuestionChange?.({ ...q, numbering: value || undefined })
  }

  // Drag-drop reorder hook - SectionedQuestionList has already updated
  // local state via setItems/setSections by the time onChange fires.
  // The microtask waits one tick so itemsRef / sectionsRef are caught
  // up to the new values before commitOrder reads them.
  const handleReorderChange = () => {
    queueMicrotask(() => commitOrder(itemsRef.current, sectionsRef.current))
  }

  return (
    <Box data-testid="bmq_root" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* Confirm bulk-delete */}
      <ConfirmModal
        open={deleteOpen}
        title="Delete questions"
        body={`Delete ${selectedCount} selected question${selectedCount !== 1 ? 's' : ''}? This cannot be undone.`}
        okLabel="Delete"
        testIdPrefix="bmq_bulkdelete"
        onConfirm={handleBulkDelete}
        onClose={() => setDeleteOpen(false)}
      />

      {/* Toolbar — mirrors BidQuestionsLeftPanel's header shape:
          [checkbox] [X questions | selected chip] [flex] [bulk actions]
          [Add Section]. */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, gap: 0.5, minHeight: 32 }}>
        <Tooltip title={allSelected ? 'Deselect all' : 'Select all questions'} disableInteractive>
          <Checkbox
            size="small"
            data-testid="bmq_selectall"
            checked={allSelected}
            indeterminate={selectedCount > 0 && !allSelected}
            onChange={toggleSelectAll}
            sx={{ p: 0.25 }}
          />
        </Tooltip>
        {selectedCount > 0 ? (
          <Chip
            data-testid="bmq_selected_chip"
            size="small"
            label={`${selectedCount} selected`}
            color="primary"
            onDelete={() => setSelectedShas(new Set())}
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        ) : (
          <Typography variant="caption" sx={{ fontSize: FS_SM, opacity: 0.6, whiteSpace: 'nowrap' }}>
            {allQuestionShas.length} question{allQuestionShas.length !== 1 ? 's' : ''}
          </Typography>
        )}
        <Tooltip title="Add Section">
          <IconButton data-testid="bmq_addsection_btn" size="small" onClick={addSection} sx={{ p: 0.25 }}>
            <CreateNewFolderOutlinedIcon sx={{ fontSize: ICON_MD }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />

        {selectedCount > 0 && (
          <>
            <Typography variant="caption" sx={{ opacity: 0.5, fontSize: FS_XS, ml: 0.5 }}>State</Typography>
            <StatePicker
              state={'todo'}
              onChange={handleBulkState}
            />
            <Typography variant="caption" sx={{ opacity: 0.5, fontSize: FS_XS, ml: 0.5 }}>Word limit</Typography>
            <TaWordLimit
              replylimit={undefined}
              onChange={handleBulkWordLimit}
            />
            <Tooltip title="Delete selected questions">
              <span>
                <IconButton
                  data-testid="bmq_bulkdelete_btn"
                  size="small"
                  color="error"
                  disabled={bulkSaving}
                  onClick={() => setDeleteOpen(true)}
                  sx={{ p: 0.25 }}
                >
                  {bulkSaving ? <CircularProgress size={14} /> : <DeleteOutlineIcon sx={{ fontSize: ICON_MD }} />}
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}


      </Box>

      {/* List - shared component handles sections + questions + drag/drop */}
      <Box data-testid="bmq_list" sx={{ flex: 1, overflowY: 'auto' }}>
        <SectionedQuestionList<Question>
          testIdPrefix="bmq"
          items={items}
          setItems={setItems}
          sections={sections}
          setSections={setSections}
          qMap={qMap}
          selectedShas={selectedShas}
          onToggleSelect={toggleSelect}
          onNumberingCommit={handleNumberingCommit}
          onDeleteSection={deleteSection}
          onChange={handleReorderChange}
          groupDragEnabled
          rowEndSlot={(q) => (
            <>
              <StatePicker
                state={q.state ?? 'todo'}
                onChange={s => onQuestionChange?.({ ...q, state: s })}
              />
              <TaWordLimit
                replylimit={q.replylimit}
                onChange={rl => onQuestionChange?.({ ...q, replylimit: rl })}
              />
            </>
          )}
        />
      </Box>
    </Box>
  )
}
